/**
 * Call store — manages LiveKit video call state globally.
 *
 * Lives in Zustand so the call persists across route navigation.
 * The LiveKit Room object is module-scoped (not reactive) — same pattern
 * as MediaRecorder in recording.ts.
 *
 * Three video modes: fullscreen, large, pip. Mode switching never
 * disconnects — it's purely a layout change.
 */

import { create } from 'zustand'
import { Room, RoomEvent, Track, ConnectionQuality } from 'livekit-client'
import { onWsEvent } from '@/stores/websocket'
import { api } from '@/api/client'
import { jLocale } from '@/lib/jurisdiction'
import { noteCapMessage } from '@/api/subscription-hooks'
import { toast } from '@/stores/toast'
import { captureMessage } from '@sentry/react'
import type { IncomingCall } from '@/api/call-types'
import {
  createRecording, appendChunks, updateRecordingStatus,
  getRecordingBlob, deleteRecording,
} from '@/lib/call-recording-db'

// Module-scoped flag — set by the screening flow when connecting as nurse
let isNurseScreeningCall = false

export type CallStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'disconnecting'
export type VideoMode = 'fullscreen' | 'large' | 'pip'
export type CallType = 'meeting' | 'screening'

/**
 * The session-created encounter linked to a patient-linked meeting (US
 * multi-state telehealth A2b). Carries the jurisdiction stamp the in-call
 * location confirm reads and writes (PUT /encounters/:uuid).
 */
export interface CallEncounter {
  uuid: string
  status: string | null
  state: string | null
  modality: string | null
  location_source: string | null
}

interface CallStore {
  // Connection
  status: CallStatus
  error: string | null

  // Call metadata
  callType: CallType | null
  meetingCode: string | null
  callId: number | null
  livekitUrl: string | null
  livekitToken: string | null

  // Patient (optional, linkable at any point)
  patientUuid: string | null

  // Session-created encounter for patient-linked meetings (A2b)
  encounter: CallEncounter | null

  // Appointment link (when started from calendar)
  appointmentId: number | null

  // Meeting sharing
  joinUrl: string | null

  // Call notes (typed during the call in the patient sidebar)
  callNotes: string
  callNoteId: number | null
  callNoteUuid: string | null
  transcribing: boolean
  transcribeError: boolean
  transcribeTimedOut: boolean

  // Video mode
  videoMode: VideoMode
  sidebarOpen: boolean

  // Media
  micEnabled: boolean
  cameraEnabled: boolean

  // Recording
  isRecording: boolean
  recordingElapsed: number

  // Incoming call
  incomingCall: IncomingCall | null

  // Connection quality
  remoteConnectionQuality: string | null
  connectionMessage: string | null

  // Participants
  participantCount: number

  // Duration
  callStartedAt: number | null
  callElapsed: number

  // Actions
  startMeeting: (opts?: { title?: string; patientId?: number; patientUuid?: string; appointmentId?: number }) => Promise<void>
  joinMeeting: (code: string) => Promise<void>
  connectToRoom: (url: string, token: string, opts?: { callType?: CallType; meetingCode?: string; callId?: number; skipRecording?: boolean }) => Promise<void>
  acceptIncomingCall: () => Promise<void>
  declineIncomingCall: () => void
  disconnect: () => Promise<void>
  setVideoMode: (mode: VideoMode) => void
  cycleVideoMode: () => void
  toggleSidebar: () => void
  toggleMic: () => void
  toggleCamera: () => void
  startCallRecording: () => void
  stopCallRecording: () => Promise<void>
  pauseCallRecording: () => void
  resumeCallRecording: () => void
  getCallRecordingBlob: () => Promise<Blob | null>
  clearCallRecording: () => void
  setPatient: (uuid: string | null) => void | Promise<void>
  setIncomingCall: (call: IncomingCall | null) => void
  setCallNotes: (text: string) => void
  getRoom: () => Room | null
  getAudioLevel: () => number
  // True while the silence watchdog believes the recording mix is digitally
  // dead (flat signal). Drives the in-call warning chip.
  recordingSilent: boolean
}

// Module-scoped — not reactive, persists across renders
let room: Room | null = null
let callTimer: ReturnType<typeof setInterval> | null = null
let recordingTimer: ReturnType<typeof setInterval> | null = null
let silenceWatchdog: ReturnType<typeof setInterval> | null = null
let silentChecks = 0
let silenceReported = false

// Note generation polling fallback — if WebSocket misses the event
let notePollingTimer: ReturnType<typeof setInterval> | null = null
let notePollingStartedAt: number | null = null

function stopNotePolling() {
  if (notePollingTimer) {
    clearInterval(notePollingTimer)
    notePollingTimer = null
  }
  notePollingStartedAt = null
}

function startNotePolling() {
  stopNotePolling()
  notePollingStartedAt = Date.now()
  notePollingTimer = setInterval(async () => {
    const { transcribing, callNoteId, callNoteUuid, patientUuid } = useCallStore.getState()
    if (!transcribing || !callNoteId) {
      stopNotePolling()
      return
    }
    // Timeout after 2 minutes
    if (notePollingStartedAt && Date.now() - notePollingStartedAt > 120_000) {
      console.log('[call] Note polling timed out after 2 minutes')
      useCallStore.setState({ transcribing: false, transcribeTimedOut: true })
      stopNotePolling()
      return
    }
    try {
      const note = await api.get<{ processing_stage: string | null; status: string }>(`/notes/${callNoteId}`)
      if (note.processing_stage === null && note.status === 'draft') {
        console.log('[call] Note generation complete (polling fallback)')
        useCallStore.setState({ transcribing: false })
        window.dispatchEvent(new CustomEvent('claro:call-note-updated', {
          detail: { noteUuid: callNoteUuid, patientUuid }
        }))
        stopNotePolling()
      }
    } catch {
      // Silently continue — retry on next interval
    }
  }, 4000)
}

// Audio recording — mixes local mic + remote audio
let audioContext: AudioContext | null = null
let mixedDestination: MediaStreamAudioDestinationNode | null = null
let callMediaRecorder: MediaRecorder | null = null
// Tracks whether the *current* pause was initiated automatically for a
// reconnect. Prevents the Reconnected handler from unpausing a user-
// initiated pause that happened to overlap a network blip.
let pausedForReconnect = false
let callRecordedChunks: Blob[] = []
let analyserNode: AnalyserNode | null = null

// IndexedDB persistence for call recordings
let currentRecordingId: number | null = null
// The durable idempotency key minted with the IndexedDB record, mirrored here
// so the end-of-call upload sends the same key the orphan-recovery path will
// reuse. Cleared on successful upload / discard.
let currentIdempotencyKey: string | null = null
let nextBatchIndex = 0
let flushTimer: ReturnType<typeof setInterval> | null = null

// CLA-465 progressive-upload state. The recording becomes server-resident as
// each flush fires — IndexedDB stays as a crash-recovery fallback. Stays
// `true` only while every chunk has either been uploaded successfully OR
// callNoteId was set when it fired (i.e. it could have been). Flips `false`
// permanently on the first failure or coverage gap; the end-of-call path
// then falls back to full-blob upload.
let progressiveUploadIntact = false

function addRemoteAudioToMix(track: { mediaStreamTrack: MediaStreamTrack }) {
  if (!audioContext || !mixedDestination) return
  try {
    const stream = new MediaStream([track.mediaStreamTrack])
    const source = audioContext.createMediaStreamSource(stream)
    source.connect(mixedDestination)
    if (analyserNode) source.connect(analyserNode)
  } catch (e) {
    console.warn('[call] Failed to add remote audio to mix:', e)
  }
}

// Debounced save of call notes to the auto-created note
let saveNoteTimer: ReturnType<typeof setTimeout> | null = null
function debounceSaveCallNote(text: string) {
  if (saveNoteTimer) clearTimeout(saveNoteTimer)
  saveNoteTimer = setTimeout(async () => {
    const { callNoteUuid, patientUuid } = useCallStore.getState()
    if (!callNoteUuid) return
    const fd = new FormData()
    fd.append('note', text)
    try {
      await api.postForm(`/notes/${callNoteUuid}/save`, fd)
      // Notify any open views to refresh
      window.dispatchEvent(new CustomEvent('claro:call-note-updated', {
        detail: { noteUuid: callNoteUuid, patientUuid }
      }))
    } catch {}
  }, 1500)
}

// Persist video mode preference
const SAVED_MODE_KEY = 'claro:video-mode'
function getSavedMode(): VideoMode {
  return (localStorage.getItem(SAVED_MODE_KEY) as VideoMode) || 'pip'
}

/**
 * CLA-465: append the chunk to the server-side note's audio file. The chunk
 * is *also* in IndexedDB — this upload is durability for cross-tab/cross-
 * crash recovery. On any failure (or if no server-side note exists yet) we
 * permanently drop out of progressive mode for this recording, and the
 * end-of-call path falls back to uploading the full assembled blob.
 */
async function uploadChunkProgressively(chunk: Blob, chunkIndex: number): Promise<void> {
  if (!progressiveUploadIntact) return
  const noteId = useCallStore.getState().callNoteId
  if (noteId == null) {
    // Coverage gap — early chunks slipped through before the note existed.
    progressiveUploadIntact = false
    return
  }
  try {
    const form = new FormData()
    form.append('audio', chunk, 'chunk.webm')
    // Stable per-recording chunk index. The api client transparently retries
    // 502s, so a chunk whose response was lost gets re-sent; the backend
    // dedupes on this index instead of appending the same audio twice into
    // the file it transcribes.
    form.append('chunk_index', String(chunkIndex))
    const res = await api.postForm<{ status?: string }>(`/notes/${noteId}/audio_append`, form)
    if (res?.status === 'gap') {
      // Backend saw a missing chunk — the progressive file can no longer be
      // trusted, so drop progressive mode and let the end-of-call path upload
      // the full assembled blob.
      console.warn('[call] audio_append reported a chunk gap; falling back to full-blob upload on stop')
      progressiveUploadIntact = false
    }
  } catch (e) {
    console.warn('[call] Progressive chunk upload failed; will fall back to full-blob upload on stop:', e)
    progressiveUploadIntact = false
  }
}

/**
 * What one call knows about its patient, booking and note. Every call starts
 * from this, so nothing from the call before can reach it (CLA-1917).
 *
 * The previous call's values are deliberately left in place after it ends:
 * the post-call panel and the note poll read them. Only `PostCallPanel`'s
 * Dismiss cleared them, and a regular call's panel hides itself without
 * dismissing, so the next call used to start holding the last call's
 * `patientUuid`, `appointmentId` and `callNoteId`. That call then appended its
 * audio to the previous call's note, or created its note on the previous
 * patient and booking.
 */
const freshCallContext = {
  patientUuid: null,
  appointmentId: null,
  callNotes: '',
  callNoteId: null,
  callNoteUuid: null,
  transcribing: false,
  transcribeError: false,
  transcribeTimedOut: false,
} satisfies Partial<CallStore>

export const useCallStore = create<CallStore>((set, get) => ({
  status: 'idle',
  error: null,
  callType: null,
  meetingCode: null,
  callId: null,
  livekitUrl: null,
  livekitToken: null,
  patientUuid: null,
  encounter: null,
  appointmentId: null,
  joinUrl: null,
  callNotes: '',
  callNoteId: null,
  callNoteUuid: null,
  transcribing: false,
  transcribeError: false,
  transcribeTimedOut: false,
  videoMode: getSavedMode(),
  sidebarOpen: false,
  micEnabled: true,
  cameraEnabled: true,
  isRecording: false,
  recordingSilent: false,
  recordingElapsed: 0,
  incomingCall: null,
  remoteConnectionQuality: null,
  connectionMessage: null,
  participantCount: 0,
  callStartedAt: null,
  callElapsed: 0,

  startMeeting: async (opts) => {
    if (get().status !== 'idle') {
      console.log('[call] startMeeting: already in state', get().status)
      return
    }
    // Set connecting immediately to prevent double-clicks
    // Clear recording chunks for brand new meetings
    callRecordedChunks = []
    set({
      ...freshCallContext,
      status: 'connecting', error: null,
      meetingCode: null, joinUrl: null, encounter: null,
      appointmentId: opts?.appointmentId ?? null,
      callElapsed: 0, callStartedAt: null,
    })

    try {
      const data = await api.post<{
        url?: string; token?: string; code?: string; join_url?: string
        encounter?: CallEncounter | null
      }>('/meetings', {
        title: opts?.title || 'Consultation',
        patient_id: opts?.patientId,
        appointment_id: opts?.appointmentId,
      })

      if (!data.url || !data.token) {
        console.error('[call] Meeting response missing url/token:', data)
        set({ status: 'idle', error: 'Meeting created but no video server details returned' })
        return
      }

      // Store join URL for sharing, the session encounter (A2b), and the
      // patient UUID if provided
      set({ joinUrl: data.join_url || null, encounter: data.encounter ?? null })
      if (opts?.patientUuid) {
        // setPatient will auto-create the note once connected
        set({ patientUuid: opts.patientUuid })
      }

      await get().connectToRoom(data.url, data.token, {
        callType: 'meeting',
        meetingCode: data.code,
      })
    } catch (e) {
      console.error('[call] startMeeting error:', e)
      set({ status: 'idle', error: e instanceof Error ? e.message : 'Failed to start meeting' })
    }
  },

  joinMeeting: async (code) => {
    if (get().status !== 'idle') return
    set({ ...freshCallContext, status: 'connecting', error: null, encounter: null })

    try {
      const data = await api.post<{
        url: string; token: string; encounter?: CallEncounter | null
      }>('/meetings/join', { code })

      set({ encounter: data.encounter ?? null })

      await get().connectToRoom(data.url, data.token, {
        callType: 'meeting',
        meetingCode: code,
      })
    } catch (e) {
      set({ status: 'idle', error: e instanceof Error ? e.message : 'Failed to join meeting' })
    }
  },

  connectToRoom: async (url, token, opts) => {
    // Disconnect any existing room cleanly — remove listeners first
    // to prevent the Disconnected event from resetting state
    const oldRoom = room
    room = null
    if (oldRoom) {
      try { oldRoom.removeAllListeners(); oldRoom.disconnect() } catch {}
    }

    const newRoom = new Room({
      adaptiveStream: true,
      dynacast: true,
    })
    room = newRoom

    // Wire up events
    newRoom.on(RoomEvent.Connected, () => {
      console.log('[call] Connected to LiveKit room')
      set({ status: 'connected', callStartedAt: Date.now() })
      // Start call timer
      if (callTimer) clearInterval(callTimer)
      callTimer = setInterval(() => {
        const started = get().callStartedAt
        if (started) set({ callElapsed: Math.floor((Date.now() - started) / 1000) })
      }, 1000)
      // Auto-start audio recording (skip if caller opted out, e.g. nurse-side screening)
      isNurseScreeningCall = !!opts?.skipRecording
      if (!opts?.skipRecording) {
        setTimeout(() => get().startCallRecording(), 500)
      }
      // Auto-create note if patient was pre-linked
      const patientUuid = get().patientUuid
      if (patientUuid && !get().callNoteId) {
        setTimeout(() => get().setPatient(patientUuid), 100)
      }
    })

    newRoom.on(RoomEvent.Disconnected, (reason) => {
      console.log('[call] Disconnected, reason:', reason)
      if (callTimer) { clearInterval(callTimer); callTimer = null }
      // Only update state if this is still the active room
      if (room === newRoom) {
        set({ status: 'idle' })
      }
    })

    newRoom.on(RoomEvent.Reconnecting, () => {
      set({ status: 'reconnecting' })
      // Pause the recording while the socket is down — otherwise the
      // MediaRecorder keeps writing silence for the gap and the final
      // recording misrepresents the call timeline.
      if (callMediaRecorder?.state === 'recording') {
        get().pauseCallRecording()
        pausedForReconnect = true
      }
    })
    newRoom.on(RoomEvent.Reconnected, () => {
      set({ status: 'connected' })
      // Only auto-resume if we're the one that paused. Don't unpause a
      // user-initiated pause that happened to coincide with a reconnect.
      if (pausedForReconnect && callMediaRecorder?.state === 'paused') {
        get().resumeCallRecording()
      }
      pausedForReconnect = false
    })

    // Belt-and-braces cleanup: when a track is unsubscribed (participant
    // left, camera/mic toggled off, reconnect churn), explicitly detach
    // LiveKit's auto-attached <audio>/<video> elements. detach() returns
    // [] if already clean, so this is idempotent with SDK auto-cleanup.
    newRoom.on(RoomEvent.TrackUnsubscribed, (track) => {
      track.detach().forEach((el) => el.remove())
    })

    newRoom.on(RoomEvent.ParticipantConnected, () => {
      set({ participantCount: (newRoom.remoteParticipants.size ?? 0) + 1 })
    })
    newRoom.on(RoomEvent.ParticipantDisconnected, () => {
      const count = (newRoom.remoteParticipants.size ?? 0) + 1
      set({ participantCount: count })
      // Auto-disconnect when the other party leaves (screening calls or 1:1 calls)
      if (newRoom.remoteParticipants.size === 0 && get().status === 'connected') {
        console.log('[call] Other participant left — auto-disconnecting in 10s')
        setTimeout(() => {
          // Recheck — they might have reconnected
          if (room === newRoom && newRoom.remoteParticipants.size === 0) {
            get().disconnect()
          }
        }, 10000)
      }
    })

    newRoom.on(RoomEvent.MediaDevicesError, (e) => {
      console.warn('[call] Media device error:', e)
    })

    newRoom.on(RoomEvent.ConnectionQualityChanged, (quality: ConnectionQuality, participant) => {
      // Only track remote participants
      if (participant.isLocal) return
      const q = quality as string
      set({ remoteConnectionQuality: q })
      if (q === ConnectionQuality.Poor) {
        set({ connectionMessage: 'Poor connection' })
      } else if (q === ConnectionQuality.Lost) {
        set({ connectionMessage: 'Connection lost' })
      } else {
        set({ connectionMessage: null })
      }
    })

    set({
      livekitUrl: url,
      livekitToken: token,
      callType: opts?.callType || null,
      meetingCode: opts?.meetingCode || null,
      callId: opts?.callId || null,
      participantCount: 1,
      callElapsed: 0,
    })

    try {
      // Request media permissions BEFORE connecting — browsers require a user gesture
      // context for the permission prompt, which can be lost after the async connect().
      // Pre-requesting also ensures the prompt appears in incognito mode.
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true })
        stream.getTracks().forEach(t => t.stop())
      } catch (e) {
        console.warn('[call] Pre-permission request failed (will try individually):', e)
      }

      console.log('[call] Connecting to', url)
      await newRoom.connect(url, token)
      console.log('[call] Connected, enabling media...')
      // Publish camera and mic — request separately so a camera denial doesn't block mic
      try {
        await newRoom.localParticipant.setMicrophoneEnabled(true)
      } catch (e) {
        console.warn('[call] Microphone access denied:', e)
        set({ micEnabled: false })
      }
      try {
        await newRoom.localParticipant.setCameraEnabled(true)
      } catch (e) {
        console.warn('[call] Camera access denied:', e)
        set({ cameraEnabled: false })
      }
    } catch (e) {
      console.error('[call] Room connect failed:', e)
      if (room === newRoom) {
        newRoom.disconnect()
        room = null
        set({ status: 'idle', error: e instanceof Error ? e.message : 'Failed to connect to video' })
      }
    }
  },

  acceptIncomingCall: async () => {
    const incoming = get().incomingCall
    if (!incoming) return

    set({ ...freshCallContext, incomingCall: null })

    // Accept on the server
    try {
      await api.post(`/telehealth/call/${incoming.callId}/accept`)
    } catch {
      // Best effort — still connect
    }

    // Set patient context from the incoming call data
    if (incoming.patientUuid) {
      set({ patientUuid: incoming.patientUuid })
    }

    await get().connectToRoom(incoming.livekitUrl, incoming.token, {
      callType: incoming.callType,
      callId: incoming.callId,
    })
  },

  declineIncomingCall: () => {
    const incoming = get().incomingCall
    if (!incoming) return
    set({ incomingCall: null })

    // Decline on the server (fire-and-forget)
    api.post(`/telehealth/call/${incoming.callId}/decline`).catch(() => {})
  },

  disconnect: async () => {
    // Stop recording before disconnecting — await to ensure chunks are flushed
    await get().stopCallRecording()

    if (callTimer) { clearInterval(callTimer); callTimer = null }

    if (room) {
      room.disconnect()
      room = null
    }

    // End screening call on the backend (resets doctor availability)
    const { callId } = get()
    if (callId) {
      api.post(`/telehealth/call/${callId}/end`, { outcome: 'completed' }).catch(() => {})
    }

    set({ status: 'idle', remoteConnectionQuality: null, connectionMessage: null, encounter: null })

    // Auto-upload recording in background — no manual step needed
    // Works with or without a patient linked. If no note exists yet, the
    // generate endpoint creates one. Doctor can associate a patient later.
    const { callNoteId, callNotes, patientUuid, appointmentId } = get()
    const doctorText = callNotes.trim()
    const hasNotes = doctorText.length > 0

    // CLA-465: if progressive upload covered the whole recording, the audio
    // is already on the server's disk via /notes/:id/audio_append. We can
    // trigger transcription without re-uploading the assembled blob.
    const useProgressiveCompletion = progressiveUploadIntact && callNoteId != null

    let blob: Blob | null = null
    let hasRecording = false
    if (!useProgressiveCompletion) {
      blob = await get().getCallRecordingBlob()
      hasRecording = !!(blob && blob.size > 1000)
    } else {
      // Progressive path treats "we have a server-side recording" as a fact.
      hasRecording = true
    }

    // Skip auto-upload for nurse-side screening calls (nurse's note is separate)
    if (isNurseScreeningCall) {
      isNurseScreeningCall = false
      // Don't upload — fall through to end
    } else if (hasRecording || hasNotes) {
      const form = new FormData()
      if (hasRecording && !useProgressiveCompletion) {
        form.append('audio', blob!, 'call-recording.webm')
      }
      form.append('treatments', '')
      form.append('clinical_type', 'auto')
      form.append('source', 'web_call')
      if (callNoteId) form.append('existing_note_id', String(callNoteId))
      if (patientUuid) form.append('patient_uuid', patientUuid)
      if (appointmentId) form.append('appointment_id', String(appointmentId))
      if (doctorText) {
        form.append('had_text', '1')
        form.append('doctor_text', doctorText)
      }

      if (get().recordingSilent) {
        // Don't pretend: the watchdog saw a dead mix, so transcription will
        // very likely find no speech. The typed notes are safe either way
        // (the backend no longer clobbers them with the error message).
        toast.error(
          'Call recording captured no audio',
          'The AI note may be empty. Any notes you typed are kept on the note.',
        )
      }
      console.log('[call] Auto-uploading', useProgressiveCompletion ? 'progressive completion (no body)' : (hasRecording ? 'recording + notes' : 'notes only'), 'in background...')
      set({ transcribing: true, transcribeError: false, transcribeTimedOut: false })
      // Reuse the recording's persisted key (falls back to a fresh one only
      // when there's no durable record, e.g. IndexedDB unavailable). This is
      // the same key orphan recovery will resend, so a crash between commit
      // and cleanup can't produce a duplicate note.
      const idempotencyKey = currentIdempotencyKey ?? crypto.randomUUID()
      api.postForm<{ note_id?: number; note_uuid?: string }>('/notes/generate?async=1', form, {
        headers: { 'Idempotency-Key': idempotencyKey },
      })
        .then((data) => {
          console.log('[call] Upload complete, transcription enqueued')
          callRecordedChunks = []
          // Clean up IndexedDB on successful upload
          if (currentRecordingId != null) {
            deleteRecording(currentRecordingId).catch(() => {})
            currentRecordingId = null
          }
          currentIdempotencyKey = null
          if (!callNoteId && data?.note_id) {
            set({ callNoteId: data.note_id, callNoteUuid: data.note_uuid || null })
          }
          startNotePolling()
        }).catch((e) => {
          console.error('[call] Background upload failed:', e)
          // Plan note-cap (CLA-847): say why, not just that it failed. The
          // recording stays in IndexedDB either way, so nothing is lost.
          const capMessage = noteCapMessage(e)
          if (capMessage) toast.error('AI note limit reached', capMessage)
          // Leave in IndexedDB for recovery on next login
          set({ transcribing: false, transcribeError: true })
        })
    }
  },

  setVideoMode: (mode) => {
    localStorage.setItem(SAVED_MODE_KEY, mode)
    set({ videoMode: mode })
  },

  cycleVideoMode: () => {
    const modes: VideoMode[] = ['pip', 'large', 'fullscreen']
    const current = get().videoMode
    const next = modes[(modes.indexOf(current) + 1) % modes.length]
    get().setVideoMode(next)
  },

  toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),

  toggleMic: () => {
    if (!room) return
    const enabled = !get().micEnabled
    room.localParticipant.setMicrophoneEnabled(enabled)
    set({ micEnabled: enabled })
  },

  toggleCamera: () => {
    if (!room) return
    const enabled = !get().cameraEnabled
    room.localParticipant.setCameraEnabled(enabled)
    set({ cameraEnabled: enabled })
  },

  startCallRecording: () => {
    if (callMediaRecorder || !room) return

    // Reset auto-pause flag so a new recording starts clean regardless
    // of what state the last one ended in.
    pausedForReconnect = false

    // Retry up to 5 times with 1s delay to wait for mic track to be published
    let attempts = 0
    const tryStart = async () => {
      attempts++
      if (!room) return

      const micPub = room.localParticipant.getTrackPublication(Track.Source.Microphone)
      const micTrack = micPub?.track?.mediaStreamTrack
      console.log(`[call] Recording attempt ${attempts}: mic track ${micTrack ? 'found' : 'not found'}`)

      if (!micTrack && attempts < 5) {
        setTimeout(tryStart, 1000)
        return
      }

      try {
        audioContext = new AudioContext()
        // An AudioContext created outside a user gesture can start
        // 'suspended' — every node in the graph then produces digital
        // silence while MediaRecorder happily writes a valid file of
        // nothing (the silent-call-recording failure, 2026-07-17). Resume
        // explicitly; the watchdog below catches anything that slips past.
        if (audioContext.state === 'suspended') {
          try { await audioContext.resume() } catch (e) { console.warn('[call] AudioContext resume failed:', e) }
        }
        console.log('[call] AudioContext state:', audioContext.state)
        mixedDestination = audioContext.createMediaStreamDestination()

        // Analyser for audio level visualisation
        analyserNode = audioContext.createAnalyser()
        analyserNode.fftSize = 256
        analyserNode.smoothingTimeConstant = 0.8

        // Silent oscillator to keep AudioContext graph active
        const silence = audioContext.createGain()
        silence.gain.value = 0
        const osc = audioContext.createOscillator()
        osc.connect(silence)
        silence.connect(mixedDestination)
        osc.start()

        // Add local mic — always use getUserMedia directly for reliability.
        // LiveKit's mediaStreamTrack goes through its audio pipeline which
        // may produce silence for the AudioContext even though audio is
        // being sent to the remote participant.
        try {
          const micStream = await navigator.mediaDevices.getUserMedia({ audio: true })
          const micSource = audioContext.createMediaStreamSource(micStream)
          micSource.connect(mixedDestination)
          if (analyserNode) micSource.connect(analyserNode)
          console.log('[call] Direct mic (getUserMedia) added to recording mix')
        } catch (e) {
          console.warn('[call] getUserMedia failed, trying LiveKit track:', e)
          if (micTrack) {
            const micStream = new MediaStream([micTrack])
            const micSource = audioContext.createMediaStreamSource(micStream)
            micSource.connect(mixedDestination)
            if (analyserNode) micSource.connect(analyserNode)
            console.log('[call] LiveKit mic track added to recording mix (fallback)')
          }
        }

        // Add existing remote audio
        room!.remoteParticipants.forEach((participant) => {
          participant.audioTrackPublications.forEach((pub) => {
            if (pub.track?.mediaStreamTrack) {
              addRemoteAudioToMix(pub.track as { mediaStreamTrack: MediaStreamTrack })
              console.log('[call] Remote audio added from', participant.identity)
            }
          })
        })

        // Listen for new remote audio tracks
        room!.on(RoomEvent.TrackSubscribed, (track) => {
          if (track.kind === Track.Kind.Audio && track.mediaStreamTrack) {
            addRemoteAudioToMix(track as { mediaStreamTrack: MediaStreamTrack })
            console.log('[call] New remote audio track added to mix')
          }
        })

        const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus' : 'audio/webm'
        callMediaRecorder = new MediaRecorder(mixedDestination.stream, { mimeType })

        callMediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) callRecordedChunks.push(e.data)
        }

        callMediaRecorder.start(1000)
        set({ isRecording: true, recordingElapsed: 0, recordingSilent: false })
        silentChecks = 0
        silenceReported = false

        // Silence watchdog — a digitally-flat analyser (every time-domain
        // sample exactly 128) for 15s+ means the mix graph is dead, not
        // that the room is quiet: real mics always carry noise floor. Warn
        // the doctor DURING the call (chip in CallControls) while they can
        // still do something about it, and report once for diagnosis.
        if (silenceWatchdog) clearInterval(silenceWatchdog)
        silenceWatchdog = setInterval(() => {
          if (!analyserNode || callMediaRecorder?.state !== 'recording') return
          const data = new Uint8Array(analyserNode.fftSize)
          analyserNode.getByteTimeDomainData(data)
          const flat = data.every((v) => v === 128)
          if (!flat) {
            silentChecks = 0
            if (useCallStore.getState().recordingSilent) set({ recordingSilent: false })
            return
          }
          silentChecks++
          if (silentChecks === 2 && audioContext?.state === 'suspended') {
            // One self-heal attempt before alarming.
            audioContext.resume().catch(() => {})
          }
          if (silentChecks >= 3 && !useCallStore.getState().recordingSilent) {
            console.error('[call] Recording mix is silent (AudioContext state:', audioContext?.state, ')')
            set({ recordingSilent: true })
            if (!silenceReported) {
              silenceReported = true
              captureMessage('call recording mix silent', {
                level: 'warning',
                tags: { feature: 'call-recording' },
                extra: {
                  audioContextState: audioContext?.state ?? 'null',
                  remoteParticipants: room?.remoteParticipants.size ?? -1,
                },
              })
            }
          }
        }, 5_000)

        if (recordingTimer) clearInterval(recordingTimer)
        recordingTimer = setInterval(() => {
          set((s) => ({ recordingElapsed: s.recordingElapsed + 1 }))
        }, 1000)

        // Create IndexedDB record for crash recovery
        createRecording({
          callId: get().callId, meetingCode: get().meetingCode,
          patientUuid: get().patientUuid, callNoteId: get().callNoteId,
          callNoteUuid: get().callNoteUuid, callNotes: get().callNotes,
          appointmentId: get().appointmentId,
        }).then(rec => {
          currentRecordingId = rec?.id ?? null
          currentIdempotencyKey = rec?.idempotencyKey ?? null
          nextBatchIndex = 0
        }).catch(() => {})

        // CLA-465: progressive-upload mode is only viable if a server-side
        // note exists from the moment of the first chunk. If callNoteId
        // arrives mid-recording (patient linked late) the early chunks are
        // server-side-missing, so the end-of-call path will fall back to
        // full-blob upload regardless. Reset on every recording-start.
        progressiveUploadIntact = get().callNoteId != null

        // Flush chunks to IndexedDB every 30s and release memory; if a
        // server-side note exists, also POST the chunk to /audio_append for
        // crash resilience and to give the server the audio progressively.
        flushTimer = setInterval(async () => {
          if (callRecordedChunks.length > 0 && currentRecordingId != null) {
            const batch = new Blob(callRecordedChunks, { type: 'audio/webm' })
            callRecordedChunks = []
            const idx = nextBatchIndex++
            await appendChunks(currentRecordingId, idx, batch).catch(() => {})
            await uploadChunkProgressively(batch, idx)
          }
        }, 30_000)

        console.log(`[call] Recording started (${micTrack ? 'LiveKit mic' : 'fallback mic'}, chunks: ${callRecordedChunks.length})`)
      } catch (e) {
        console.error('[call] Failed to start recording:', e)
      }
    }

    tryStart()
  },

  stopCallRecording: async () => {
    if (recordingTimer) { clearInterval(recordingTimer); recordingTimer = null }
    if (flushTimer) { clearInterval(flushTimer); flushTimer = null }
    if (silenceWatchdog) { clearInterval(silenceWatchdog); silenceWatchdog = null }

    if (callMediaRecorder && callMediaRecorder.state !== 'inactive') {
      await new Promise<void>((resolve) => {
        callMediaRecorder!.onstop = () => resolve()
        callMediaRecorder!.stop()
      })
    }
    callMediaRecorder = null

    // Flush remaining in-memory chunks to IndexedDB + server (CLA-465)
    if (currentRecordingId != null && callRecordedChunks.length > 0) {
      const batch = new Blob(callRecordedChunks, { type: 'audio/webm' })
      callRecordedChunks = []
      const idx = nextBatchIndex++
      await appendChunks(currentRecordingId, idx, batch)
      await uploadChunkProgressively(batch, idx)
    }

    if (currentRecordingId != null) {
      await updateRecordingStatus(currentRecordingId, 'pending_upload')
    }

    if (audioContext) {
      audioContext.close().catch(() => {})
      audioContext = null
      mixedDestination = null
      analyserNode = null
    }
    set({ isRecording: false })
    console.log('[call] Recording stopped, chunks flushed to IndexedDB')
  },

  pauseCallRecording: () => {
    if (callMediaRecorder?.state === 'recording') {
      callMediaRecorder.pause()
      if (recordingTimer) { clearInterval(recordingTimer); recordingTimer = null }
      console.log('[call] Recording paused')
    }
  },

  resumeCallRecording: () => {
    if (callMediaRecorder?.state === 'paused') {
      callMediaRecorder.resume()
      recordingTimer = setInterval(() => {
        set((s) => ({ recordingElapsed: s.recordingElapsed + 1 }))
      }, 1000)
      console.log('[call] Recording resumed')
    }
  },

  getCallRecordingBlob: async () => {
    // Prefer IndexedDB (has all flushed batches); fall back to in-memory chunks
    if (currentRecordingId != null) {
      const blob = await getRecordingBlob(currentRecordingId)
      if (blob) return blob
    }
    if (callRecordedChunks.length === 0) return null
    return new Blob(callRecordedChunks, { type: 'audio/webm' })
  },

  clearCallRecording: () => {
    callRecordedChunks = []
    if (currentRecordingId != null) {
      deleteRecording(currentRecordingId).catch(() => {})
      currentRecordingId = null
    }
    currentIdempotencyKey = null
  },

  setPatient: async (uuid) => {
    set({ patientUuid: uuid })
    // Auto-create a note when patient links during a connected call
    if (uuid && get().status === 'connected' && !get().callNoteId) {
      try {
        const title = `Call — ${new Date().toLocaleTimeString(jLocale(), { hour: '2-digit', minute: '2-digit' })}`
        const apptId = get().appointmentId
        const note = await api.post<{ id: number; uuid: string }>('/notes', {
          note: '',
          title,
          source: 'web_call',
          appointment_id: apptId || undefined,
          patient_uuid: uuid,
        })
        set({ callNoteId: note.id, callNoteUuid: note.uuid })
        // Link patient to the note
        await api.post(`/notes/${note.uuid}/patient`, { patient_uuid: uuid })
        console.log('[call] Auto-created note', note.uuid, 'for patient', uuid)
        // Notify React Query to refresh notes lists
        window.dispatchEvent(new CustomEvent('claro:call-note-created', { detail: { noteUuid: note.uuid, patientUuid: uuid } }))
      } catch (e) {
        console.warn('[call] Failed to auto-create note:', e)
      }
    }
  },

  setIncomingCall: (call) => set({ incomingCall: call }),

  setCallNotes: (text) => {
    set({ callNotes: text })
    // Debounced save to the call note
    debounceSaveCallNote(text)
  },

  getRoom: () => room,

  getAudioLevel: () => {
    if (!analyserNode) return 0
    const data = new Uint8Array(analyserNode.frequencyBinCount)
    analyserNode.getByteFrequencyData(data)
    const sum = data.reduce((a, b) => a + b, 0)
    return Math.min(1, (sum / data.length) / 128)
  },
}))

// Handle all call-related WebSocket events (unified via the main app WebSocket)
onWsEvent((event) => {
  const data = event as Record<string, unknown>
  const type = data.type as string
  const state = useCallStore.getState()

  switch (type) {
    case 'call.incoming': {
      const callData = (data.data || data) as Record<string, unknown>
      const incoming = {
        callId: (callData.call_id as number) || 0,
        roomName: (callData.room_name as string) || '',
        token: (callData.token as string) || (callData.livekit_token as string) || '',
        livekitUrl: (callData.livekit_url as string) || '',
        callerName: (callData.caller_name as string) || 'Unknown',
        callType: 'screening' as const,
        patientName: (callData.patient_name as string) || undefined,
        patientUuid: (callData.patient_uuid as string) || undefined,
        treatmentSummary: (callData.treatment_summary as string) || undefined,
        treatmentPlanId: (callData.treatment_plan_id as number) || undefined,
      }
      if (incoming.token && incoming.livekitUrl) {
        state.setIncomingCall(incoming)
        playRingSound()
      }
      break
    }

    case 'call.matched': {
      // The queued half of a screening call: the nurse asked for a doctor,
      // none was free, and process_queue has now paired her with one. The
      // doctor is already ringing; connect the nurse the same way the
      // immediate 'calling' path in ScreeningFlow does.
      const callData = (data.data || data) as Record<string, unknown>
      const token = (callData.token as string) || ''
      const livekitUrl = (callData.livekit_url as string) || ''
      if (!token || !livekitUrl) break
      if (state.status !== 'idle') {
        console.warn('[call] call.matched arrived while a call is active — ignoring')
        break
      }
      const patientUuid = callData.patient_uuid as string | undefined
      useCallStore.setState(freshCallContext)
      if (patientUuid) state.setPatient(patientUuid)
      window.dispatchEvent(new CustomEvent('claro:call-event', {
        detail: { type, ...callData }
      }))
      state.connectToRoom(livekitUrl, token, {
        callType: 'screening',
        callId: callData.call_id as number | undefined,
        skipRecording: true,
      }).catch((e) => {
        console.error('[call] Failed to join matched screening call', e)
      })
      break
    }

    case 'call.answered':
    case 'call.cancelled': {
      if (state.incomingCall) {
        state.setIncomingCall(null)
      }
      break
    }

    case 'meeting.patient_waiting':
    case 'meeting.patient_joined': {
      // Forward to WaitingParticipants component via custom event
      const eventData = (data.data || data) as Record<string, unknown>
      window.dispatchEvent(new CustomEvent('claro:call-event', {
        detail: { type, ...eventData }
      }))
      break
    }

    case 'note_changed':
    case 'note_generated': {
      if (!state.transcribing || !state.callNoteId) return
      const eventNoteId = data.note_id as number
      if (eventNoteId !== state.callNoteId) return
      console.log('[call] Transcription complete (WebSocket event)')
      stopNotePolling()
      useCallStore.setState({ transcribing: false })
      window.dispatchEvent(new CustomEvent('claro:call-note-updated', {
        detail: { noteUuid: state.callNoteUuid, patientUuid: state.patientUuid }
      }))
      break
    }
  }
})

function playRingSound() {
  try {
    const ctx = new AudioContext()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.frequency.value = 440
    gain.gain.value = 0.1
    osc.start()
    setTimeout(() => { gain.gain.value = 0 }, 500)
    setTimeout(() => { gain.gain.value = 0.1 }, 1000)
    setTimeout(() => { gain.gain.value = 0 }, 1500)
    setTimeout(() => { osc.stop(); ctx.close() }, 2000)
  } catch { /* audio not available */ }
}

export function formatCallDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
  return `${m}:${s.toString().padStart(2, '0')}`
}
